<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.modules.title') }}
          </h1>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button @click="openCreateModal()" class="btn btn-sm btn-primary">
            <i class="fas fa-plus me-1"></i> {{ $t('landlord.modules.create') }}
          </button>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">
        <div class="card card-flush">
          <div class="card-body py-4">
            <div v-if="loading" class="text-center py-10">
              <span class="spinner-border text-primary"></span>
            </div>
            <div v-else-if="!modules.length" class="text-center py-10 text-gray-500">
              {{ $t('landlord.modules.no_results') }}
            </div>
            <div v-else>
              <!-- Module tree -->
              <div v-for="mod in modules" :key="mod.id" class="mb-6">
                <div class="d-flex align-items-center justify-content-between bg-light rounded p-4">
                  <div class="d-flex align-items-center">
                    <i :class="mod.icon || 'fas fa-puzzle-piece'" class="fs-2 text-primary me-4"></i>
                    <div>
                      <div class="fw-bolder fs-5">{{ mod.name }}</div>
                      <div class="text-gray-500 fs-7">{{ mod.slug }}</div>
                      <div v-if="mod.description" class="text-muted fs-8 mt-1">{{ mod.description }}</div>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge" :class="mod.is_active ? 'badge-light-success' : 'badge-light-danger'">
                      {{ mod.is_active ? $t('landlord.modules.active') : $t('landlord.modules.inactive') }}
                    </span>
                    <button @click="openCreateModal(mod.id)" class="btn btn-icon btn-sm btn-light-success" :title="$t('landlord.modules.add_child')">
                      <i class="fas fa-plus"></i>
                    </button>
                    <button @click="openEditModal(mod)" class="btn btn-icon btn-sm btn-light-primary" :title="$t('landlord.modules.edit')">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button @click="onDelete(mod)" class="btn btn-icon btn-sm btn-light-danger" :title="$t('landlord.modules.delete')">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <!-- Children -->
                <div v-if="mod.children && mod.children.length" class="ms-10 mt-3">
                  <div v-for="child in mod.children" :key="child.id" class="d-flex align-items-center justify-content-between border rounded p-3 mb-2">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-angle-right text-gray-400 me-3"></i>
                      <div>
                        <div class="fw-bold">{{ child.name }}</div>
                        <div class="text-gray-500 fs-8">{{ child.slug }}</div>
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="badge" :class="child.is_active ? 'badge-light-success' : 'badge-light-danger'">
                        {{ child.is_active ? $t('landlord.modules.active') : $t('landlord.modules.inactive') }}
                      </span>
                      <button @click="openEditModal(child)" class="btn btn-icon btn-sm btn-light-primary btn-sm" style="width:28px;height:28px;">
                        <i class="fas fa-edit fs-8"></i>
                      </button>
                      <button @click="onDelete(child)" class="btn btn-icon btn-sm btn-light-danger btn-sm" style="width:28px;height:28px;">
                        <i class="fas fa-trash fs-8"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal fade" id="moduleModal" data-bs-backdrop="static" tabindex="-1">
      <div class="modal-dialog">
        <form @submit.prevent="onSubmit">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? $t('landlord.modules.edit_title') : $t('landlord.modules.create_title') }}</h5>
              <button type="button" class="btn-close" @click="closeModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-4">
                <label class="form-label fw-bold required">{{ $t('landlord.modules.field_slug') }}</label>
                <input v-model="form.slug" type="text" class="form-control form-control-solid" :disabled="isEditing" placeholder="ex: pos.sales" />
              </div>
              <div class="mb-4">
                <label class="form-label fw-bold required">{{ $t('landlord.modules.field_name') }}</label>
                <input v-model="form.name" type="text" class="form-control form-control-solid" />
              </div>
              <div class="mb-4">
                <label class="form-label fw-bold">{{ $t('landlord.modules.field_description') }}</label>
                <textarea v-model="form.description" class="form-control form-control-solid" rows="2"></textarea>
              </div>
              <div class="row">
                <div class="col-md-6 mb-4">
                  <label class="form-label fw-bold">{{ $t('landlord.modules.field_icon') }}</label>
                  <input v-model="form.icon" type="text" class="form-control form-control-solid" placeholder="fas fa-box" />
                </div>
                <div class="col-md-6 mb-4">
                  <label class="form-label fw-bold">{{ $t('landlord.modules.field_sort_order') }}</label>
                  <input v-model.number="form.sort_order" type="number" class="form-control form-control-solid" />
                </div>
              </div>
              <div class="mb-4">
                <label class="form-label fw-bold">{{ $t('landlord.modules.field_parent') }}</label>
                <select v-model="form.parent_id" class="form-select form-select-solid">
                  <option :value="null">-- {{ $t('landlord.modules.no_parent') }} --</option>
                  <option v-for="m in modules" :key="m.id" :value="m.id">{{ m.name }} ({{ m.slug }})</option>
                </select>
              </div>
              <div class="form-check form-switch">
                <input v-model="form.is_active" class="form-check-input" type="checkbox" id="moduleActiveSwitch" />
                <label class="form-check-label" for="moduleActiveSwitch">{{ $t('landlord.modules.active') }}</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" @click="closeModal" class="btn btn-light">{{ $t('landlord.modules.cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ isEditing ? $t('landlord.modules.save') : $t('landlord.modules.create') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'LandlordModulesIndex',
    data() {
        return {
            loading: false,
            submitting: false,
            isEditing: false,
            editingId: null,
            form: this.emptyForm(),
            modalInstance: null,
        }
    },
    computed: {
        ...mapGetters('landlord', ['modules']),
    },
    mounted() {
        this.loadModules()
    },
    methods: {
        ...mapActions('landlord', ['fetchModules', 'createModule', 'updateModule', 'deleteModule']),

        emptyForm() {
            return { slug: '', name: '', description: '', icon: '', sort_order: 0, parent_id: null, is_active: true }
        },

        loadModules() {
            this.loading = true
            this.fetchModules().finally(() => { this.loading = false })
        },

        openCreateModal(parentId = null) {
            this.isEditing = false
            this.editingId = null
            this.form = this.emptyForm()
            this.form.parent_id = parentId
            this.showModal()
        },

        openEditModal(mod) {
            this.isEditing = true
            this.editingId = mod.id
            this.form = {
                slug: mod.slug,
                name: mod.name,
                description: mod.description || '',
                icon: mod.icon || '',
                sort_order: mod.sort_order || 0,
                parent_id: mod.parent_id,
                is_active: mod.is_active,
            }
            this.showModal()
        },

        showModal() {
            if (!this.modalInstance) {
                this.modalInstance = new bootstrap.Modal(document.getElementById('moduleModal'))
            }
            this.modalInstance.show()
        },

        closeModal() {
            if (this.modalInstance) {
                this.modalInstance.hide()
            }
        },

        onSubmit() {
            this.submitting = true
            const action = this.isEditing
                ? this.updateModule({ moduleId: this.editingId, data: this.form })
                : this.createModule(this.form)

            action.then(() => {
                window.toastr['success'](
                    this.isEditing
                        ? i18n.global.t('landlord.modules.updated')
                        : i18n.global.t('landlord.modules.created')
                )
                this.closeModal()
                this.loadModules()
            }).finally(() => {
                this.submitting = false
            })
        },

        onDelete(mod) {
            Swal.fire({
                text: this.$t('landlord.modules.confirm_delete', { name: mod.name }),
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-danger',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (result.value) {
                    this.deleteModule(mod.id).then(() => {
                        window.toastr['success'](i18n.global.t('landlord.modules.deleted'))
                        this.loadModules()
                    })
                }
            })
        },
    }
}
</script>
